import mermaid from 'mermaid';
import React, { useEffect, useRef, useState } from 'react';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';
import { exportSvgToPng } from './mermaidUtils';
import { MermaidToolbar } from './MermaidToolbar';
import { useChatStore, ChatState } from '../../../store/chatStore';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../codeBlockUtils';

import { useTranslation } from 'react-i18next';

interface MermaidProps {
  code: string;
  className?: string;
}

type MermaidTheme = 'default' | 'dark' | 'forest' | 'neutral';

export const Mermaid: React.FC<MermaidProps> = ({ code }) => {
  const { t } = useTranslation('pleri/chat');
  const containerRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);
  const idRef = useRef<string>(
    `mermaid-${Math.random().toString(36).substr(2, 9)}`,
  );
  const [theme] = useState<MermaidTheme>('default');
  const renderSeqRef = useRef(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const [hasError, setHasError] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);
  const [capturedErrorMessage, setCapturedErrorMessage] = useState<
    string | null
  >(null);
  const [retryCounter, setRetryCounter] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showErrorUI, setShowErrorUI] = useState(false);

  // Mermaid's grammar rejects trailing whitespace after statements such as
  // `subgraph X["..."]` and `direction TB` ("Expecting 'NEWLINE', got 'SPACE'"),
  // so strip end-of-line whitespace regardless of where the code came from.
  const normalizedCode = code.replace(/[^\S\n]+$/gm, '');

  const activeConversationId = useChatStore(
    (state: ChatState) => state.activeConversationId,
  );
  const addMessage = useChatStore((state: ChatState) => state.addMessage);
  const createConversation = useChatStore(
    (state: ChatState) => state.createConversation,
  );

  useEffect(() => {
    const currentCode = normalizedCode;

    if (!currentCode) {
      // Invalidate any in-flight render so it can't repopulate the container.
      renderSeqRef.current += 1;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      return;
    }

    // The previous SVG stays in place until a newer render succeeds
    // (double-buffering) — wiping the container up front made the diagram
    // flash blank on every streamed chunk.
    const seq = ++renderSeqRef.current;

    const render = async () => {
      try {
        mermaid.initialize({
          startOnLoad: true,
          theme,
          suppressErrorRendering: true,
        });
        // Unique id per attempt: concurrent mermaid.render calls sharing an
        // id clobber each other's scratch DOM nodes.
        const svgCode = await mermaid.render(
          `${idRef.current}-${seq}`,
          currentCode,
        );
        // A newer attempt started while this one was in flight — discard.
        if (seq !== renderSeqRef.current || !containerRef.current) {
          return;
        }
        containerRef.current.innerHTML = svgCode.svg;
        const svg = containerRef.current.querySelector('svg');
        if (svg) {
          svg.style.maxWidth = '100%';
          svg.style.maxHeight = '100%';
          svg.style.display = 'block';
        }
        setHasRendered(true);
        setHasError(false);
        setCapturedErrorMessage(null);
        setShowErrorUI(false);
      } catch (err: unknown) {
        if (seq !== renderSeqRef.current) {
          return;
        }
        console.log(err);
        setHasError(true);
        if (err instanceof Error) {
          setCapturedErrorMessage(err.message);
        } else {
          setCapturedErrorMessage(String(err));
        }
      }
    };

    // First attempt renders immediately; while the code is still changing
    // (streamed chunks / text animation) subsequent attempts are debounced so
    // partial diagrams aren't parsed on every keystroke-sized update.
    const timer = setTimeout(render, seq === 1 ? 0 : 300);
    return () => {
      clearTimeout(timer);
    };
  }, [normalizedCode, theme, retryCounter]);

  useEffect(() => {
    // Clear any existing timeout when dependencies change or on unmount
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (hasError) {
      retryTimeoutRef.current = setTimeout(() => {
        setShowErrorUI(true);
        setRetryCounter((prev) => prev + 1);
      }, 2000);
    } else {
      setShowErrorUI(false);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [hasError, normalizedCode]);

  const handleDownloadPNG = async () => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) {
      console.warn('SVG element not found for download in Mermaid component.');
      return;
    }
    exportSvgToPng(svg);
  };

  const handleResetTransform = () => {
    if (transformComponentRef.current) {
      transformComponentRef.current.resetTransform();
    }
  };

  const handleToggleMaximize = () => {
    setIsMaximized(!isMaximized);
    // Optional: Reset zoom/pan when maximizing/minimizing
    // setTimeout(() => handleResetTransform(), 0);
  };

  const handleAskToFixError = () => {
    if (!code) {
      return;
    }

    const errorMessageText =
      capturedErrorMessage ||
      'Mermaid diagram failed to render. Specific error unknown.';
    const messageText = `<usermessage>Help me fix this Mermaid diagram that failed to render.</usermessage>\n\n<context><followup>The diagram code that failed is:\n\`\`\`mermaid\n${normalizedCode}\n\`\`\`\nThe error message was: "${errorMessageText}". Please regenerate the same diagram with same content. Don't try another unless I ask for it.</followup></context>`;

    if (!activeConversationId) {
      const newId = createConversation();
      addMessage(newId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    } else {
      addMessage(activeConversationId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    }
  };

  const baseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition:
      'width 0.3s ease, height 0.3s ease, top 0.3s ease, left 0.3s ease',
  };

  const normalStyle: React.CSSProperties = {
    ...baseStyle,
    position: 'relative',
    height: '350px',
    padding: '20px',
  };

  const maximizedStyle: React.CSSProperties = {
    ...baseStyle,
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '90vh',
    zIndex: 1000,
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)', // Add some shadow for better layering
    padding: '20px', // Keep padding consistent or adjust as needed
  };

  return (
    <div
      style={isMaximized ? maximizedStyle : normalStyle}
      className={
        isMaximized
          ? 'rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
          : 'relative mx-4 w-auto rounded-md border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
      }
      ref={parentContainerRef}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {isHovering && (
        <MermaidToolbar
          transformRef={transformComponentRef}
          handleDownloadPNG={handleDownloadPNG}
          handleReset={handleResetTransform}
          isMaximized={isMaximized}
          onToggleMaximize={handleToggleMaximize}
        />
      )}
      <TransformWrapper
        ref={transformComponentRef}
        initialScale={1}
        minScale={0.5}
        maxScale={5}
        wheel={{ disabled: true }}
        doubleClick={{ disabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ width: '100%', height: '100%' }}
        >
          <div
            ref={containerRef}
            id="mermaid-svg-container"
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </TransformComponent>
      </TransformWrapper>
      {!hasRendered && !showErrorUI && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
            <Spinner className="text-gray-400 dark:text-gray-500" />
            {t('renderingDiagram')}
          </div>
        </div>
      )}
      {showErrorUI && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10, // Ensure it's above diagram content
            padding: '20px',
            boxSizing: 'border-box',
            borderRadius: isMaximized ? '0.5rem' : '0.375rem', // Matches parent's rounded-lg/rounded-md
            flexDirection: 'column', // Allow button to be below text
          }}
          className="bg-white/85 text-center dark:bg-gray-900/85"
        >
          <pre
            className="text-base font-medium text-red-600"
            style={{ margin: 0, marginBottom: '16px' }}
          >
            {t('failedToRenderDiagram')}
          </pre>
          <button
            onClick={handleAskToFixError}
            className="mt-4 flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <SparklesIcon className="mr-2 h-5 w-5 text-orange-500" />
            {t('askPleriToFix')}
          </button>
        </div>
      )}
    </div>
  );
};
